<template>
  <div v-if="page" class="legal-page">
    <div class="legal-page__ambient legal-page__ambient--primary"></div>
    <div class="legal-page__ambient legal-page__ambient--secondary"></div>

    <div class="legal-page__shell">
      <section class="legal-page__hero">
        <nav class="legal-page__breadcrumb" aria-label="Breadcrumb">
          <a href="/">Home</a>
          <span>/</span>
          <span class="legal-page__breadcrumb-current">{{ page.title }}</span>
        </nav>

        <div class="legal-page__hero-grid">
          <div class="legal-page__hero-copy">
            <p class="legal-page__eyebrow">{{ page.eyebrow }}</p>
            <h1 class="legal-page__title">{{ page.title }}</h1>
            <p class="legal-page__intro">{{ page.intro }}</p>
          </div>

          <aside class="legal-page__hero-panel" aria-label="Page summary">
            <div class="legal-page__stat-grid">
              <div class="legal-page__stat">
                <span class="legal-page__stat-value">{{ formattedSections.length }}</span>
                <span class="legal-page__stat-label">Sections</span>
              </div>
              <div class="legal-page__stat">
                <span class="legal-page__stat-value">Sitewide</span>
                <span class="legal-page__stat-label">Coverage</span>
              </div>
              <div class="legal-page__stat">
                <span class="legal-page__stat-value">Global</span>
                <span class="legal-page__stat-label">Audience</span>
              </div>
            </div>
            <p class="legal-page__hero-note">{{ page.description }}</p>
          </aside>
        </div>
      </section>

      <div class="legal-page__layout">
        <article class="legal-page__article">
          <section
            v-for="section in formattedSections"
            :id="section.id"
            :key="section.id"
            class="legal-page__section"
          >
            <div class="legal-page__section-index">{{ section.number }}</div>
            <div class="legal-page__section-body">
              <h2 class="legal-page__section-title">{{ section.heading }}</h2>
              <p
                v-for="(paragraph, paragraphIndex) in section.paragraphs"
                :key="`${section.id}-${paragraphIndex}`"
                class="legal-page__paragraph"
              >
                {{ paragraph }}
              </p>
            </div>
          </section>
        </article>

        <aside class="legal-page__sidebar">
          <nav class="legal-page__panel" aria-label="On this page">
            <p class="legal-page__panel-label">On This Page</p>
            <a
              v-for="section in formattedSections"
              :key="`nav-${section.id}`"
              :href="`#${section.id}`"
              class="legal-page__toc-link"
            >
              {{ section.heading }}
            </a>
          </nav>

          <section class="legal-page__panel" aria-label="Contact GamesNest">
            <p class="legal-page__panel-label">Need To Reach Us?</p>
            <p class="legal-page__panel-copy">
              For corrections, privacy questions, or partnership requests, email
              GamesNest directly.
            </p>
            <a class="legal-page__panel-action" href="mailto:contact@gamesnest.org">
              contact@gamesnest.org
            </a>
          </section>
        </aside>
      </div>

      <section class="legal-page__directory" aria-label="Legal directory">
        <p class="legal-page__directory-label">Legal Directory</p>
        <h2 class="legal-page__directory-title">Explore the rest of the policy pages</h2>

        <div class="legal-page__directory-grid">
          <a
            v-for="entry in relatedPages"
            :key="entry.key"
            :href="entry.path"
            class="legal-page__directory-card"
          >
            <p class="legal-page__directory-eyebrow">{{ entry.eyebrow }}</p>
            <h3 class="legal-page__directory-card-title">{{ entry.title }}</h3>
            <p class="legal-page__directory-description">{{ entry.description }}</p>
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { LEGAL_PAGE_KEYS, getLegalPage } from '@/data/legal/pages'

export default {
  name: 'LegalPage',
  computed: {
    pageKey() {
      return this.$route.meta.legalKey || ''
    },
    page() {
      return getLegalPage(this.pageKey)
    },
    formattedSections() {
      if (!this.page) return []

      return this.page.sections.map((section, index) => ({
        ...section,
        id: this.buildSectionId(section.heading, index),
        number: this.formatSectionIndex(index),
      }))
    },
    relatedPages() {
      return LEGAL_PAGE_KEYS
        .filter(key => key !== this.pageKey)
        .map(key => {
          const legalPage = getLegalPage(key)
          if (!legalPage) return null

          return {
            ...legalPage,
            key,
            path: this.getLegalPath(key),
          }
        })
        .filter(Boolean)
    },
  },
  methods: {
    buildSectionId(heading, index) {
      const normalizedHeading = heading
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

      if (normalizedHeading) {
        return `${this.pageKey}-${normalizedHeading}`
      }

      return `${this.pageKey}-section-${index + 1}`
    },
    formatSectionIndex(index) {
      return String(index + 1).padStart(2, '0')
    },
    getLegalPath(key) {
      return `/${key}/`
    },
  },
  metaInfo() {
    if (!this.page) return {}

    return {
      title: this.page.title,
      meta: [
        {
          name: 'description',
          content: this.page.description,
        },
      ],
    }
  },
}
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;

.legal-page {
  position: relative;
  padding: rem(40) rem(24) rem(96);
  overflow: hidden;

  @include pc {
    padding: rem(72) rem(24) rem(120);
  }

  &__ambient {
    position: fixed;
    border-radius: 50%;
    filter: blur(rem(90));
    pointer-events: none;
    z-index: 0;
  }

  &__ambient--primary {
    top: rem(24);
    left: -15%;
    width: rem(420);
    height: rem(420);
    background: rgba($primary-color, 0.16);
  }

  &__ambient--secondary {
    right: -10%;
    bottom: rem(40);
    width: rem(360);
    height: rem(360);
    background: rgba($secondary-color, 0.12);
  }

  &__shell {
    position: relative;
    z-index: 1;
    max-width: $container-max-width;
    margin: 0 auto;
  }

  &__hero {
    margin-bottom: rem(28);
    padding: rem(28) rem(24);
    border: 1px solid rgba($primary-color, 0.2);
    border-radius: rem(28);
    background: linear-gradient(135deg, rgba($bg-secondary, 0.95), rgba($bg-primary, 0.98));
    box-shadow: 0 rem(28) rem(60) rgba(0, 0, 0, 0.3);

    @include pc {
      margin-bottom: rem(40);
      padding: rem(40) rem(36);
    }
  }

  &__breadcrumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: rem(8);
    margin-bottom: rem(22);
    color: $text-muted;
    font-size: rem(13);
    letter-spacing: 0.04em;

    a {
      color: $secondary-color;
      transition: color $transition-speed;

      &:hover {
        color: $text-primary;
      }
    }
  }

  &__breadcrumb-current {
    color: $text-primary;
  }

  &__hero-grid {
    display: grid;
    gap: rem(24);

    @include pc {
      grid-template-columns: minmax(0, 1.7fr) minmax(rem(300), rem(340));
      align-items: start;
    }
  }

  &__eyebrow {
    display: inline-flex;
    align-items: center;
    margin: 0 0 rem(16);
    padding: rem(8) rem(16);
    border: 1px solid rgba($primary-color, 0.35);
    border-radius: rem(999);
    background: rgba($primary-color, 0.12);
    color: $primary-color;
    font-family: $font-family-heading;
    font-size: rem(12);
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  &__title {
    margin: 0 0 rem(18);
    max-width: rem(760);
    color: $text-primary;
    font-family: $font-family-heading;
    font-size: rem(36);
    font-weight: 900;
    line-height: 1.08;

    @include pc {
      font-size: rem(56);
    }
  }

  &__intro {
    margin: 0;
    max-width: rem(800);
    color: $text-secondary;
    font-size: rem(17);
    line-height: 1.8;

    @include pc {
      font-size: rem(18);
    }
  }

  &__hero-panel {
    padding: rem(22);
    border: 1px solid rgba($secondary-color, 0.22);
    border-radius: rem(24);
    background: linear-gradient(180deg, rgba($bg-tertiary, 0.95), rgba($bg-secondary, 0.88));
    backdrop-filter: blur(rem(18));
  }

  &__stat-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: rem(10);
    margin-bottom: rem(18);
  }

  &__stat {
    padding: rem(14) rem(10);
    border: 1px solid rgba($border-color, 1);
    border-radius: rem(18);
    background: rgba($bg-primary, 0.7);
    text-align: center;
  }

  &__stat-value {
    display: block;
    margin-bottom: rem(6);
    color: $text-primary;
    font-family: $font-family-heading;
    font-size: rem(20);
    font-weight: 800;
    line-height: 1.1;
  }

  &__stat-label {
    display: block;
    color: $text-muted;
    font-size: rem(11);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__hero-note {
    margin: 0;
    color: $text-secondary;
    font-size: rem(14);
    line-height: 1.72;
  }

  &__layout {
    display: grid;
    gap: rem(24);

    @include pc {
      grid-template-columns: minmax(0, 1fr) rem(300);
      align-items: start;
    }
  }

  &__article {
    display: grid;
    gap: rem(18);
  }

  &__section {
    display: grid;
    gap: rem(16);
    padding: rem(24);
    border: 1px solid rgba($border-color, 1);
    border-radius: rem(24);
    background: linear-gradient(180deg, rgba($bg-secondary, 0.95), rgba($bg-primary, 0.98));
    box-shadow: 0 rem(16) rem(32) rgba(0, 0, 0, 0.22);
    scroll-margin-top: rem(104);

    @include pc {
      grid-template-columns: rem(80) minmax(0, 1fr);
      gap: rem(24);
      padding: rem(28) rem(30);
    }
  }

  &__section-index {
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem(52);
    height: rem(52);
    border: 1px solid rgba($primary-color, 0.3);
    border-radius: rem(16);
    background: linear-gradient(135deg, rgba($primary-color, 0.15), rgba($secondary-color, 0.1));
    color: $primary-color;
    font-family: $font-family-heading;
    font-size: rem(20);
    font-weight: 900;
    letter-spacing: 0.08em;

    @include pc {
      width: rem(68);
      height: rem(68);
      font-size: rem(24);
    }
  }

  &__section-title {
    margin: 0 0 rem(16);
    color: $text-primary;
    font-family: $font-family-heading;
    font-size: rem(22);
    font-weight: 800;
    line-height: 1.2;

    @include pc {
      font-size: rem(28);
    }
  }

  &__paragraph {
    margin: 0;
    color: $text-secondary;
    font-size: rem(15);
    line-height: 1.85;

    @include pc {
      font-size: rem(16);
    }

    & + & {
      margin-top: rem(14);
    }
  }

  &__sidebar {
    display: grid;
    gap: rem(16);

    @include pc {
      position: sticky;
      top: rem(88);
    }
  }

  &__panel {
    padding: rem(22);
    border: 1px solid rgba($border-color, 1);
    border-radius: rem(22);
    background: linear-gradient(180deg, rgba($bg-tertiary, 0.95), rgba($bg-secondary, 0.88));
  }

  &__panel-label {
    margin: 0 0 rem(16);
    color: $text-primary;
    font-family: $font-family-heading;
    font-size: rem(17);
    font-weight: 800;
    line-height: 1.2;
  }

  &__toc-link {
    display: block;
    padding: rem(12) rem(14);
    border: 1px solid transparent;
    border-radius: rem(14);
    color: $text-secondary;
    font-size: rem(14);
    line-height: 1.45;
    transition: transform $transition-speed, color $transition-speed, border-color $transition-speed, background $transition-speed;

    & + & {
      margin-top: rem(6);
    }

    &:hover {
      color: $text-primary;
      border-color: rgba($secondary-color, 0.3);
      background: rgba($secondary-color, 0.08);
      transform: translateX(rem(4));
    }
  }

  &__panel-copy {
    margin: 0 0 rem(16);
    color: $text-secondary;
    font-size: rem(14);
    line-height: 1.72;
  }

  &__panel-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: rem(48);
    padding: rem(12) rem(16);
    border: 1px solid rgba($secondary-color, 0.35);
    border-radius: rem(14);
    background: rgba($secondary-color, 0.1);
    color: $text-primary;
    font-family: $font-family-heading;
    font-size: rem(14);
    font-weight: 700;
    overflow-wrap: anywhere;
    transition: transform $transition-speed, border-color $transition-speed, background $transition-speed;

    &:hover {
      transform: translateY(rem(-2));
      border-color: rgba($secondary-color, 0.5);
      background: rgba($secondary-color, 0.16);
    }
  }

  &__directory {
    margin-top: rem(40);
    padding: rem(28) rem(24);
    border: 1px solid rgba($border-color, 1);
    border-radius: rem(28);
    background: linear-gradient(180deg, rgba($bg-secondary, 0.95), rgba($bg-primary, 0.98));

    @include pc {
      padding: rem(36);
    }
  }

  &__directory-label {
    margin: 0 0 rem(10);
    color: $primary-color;
    font-family: $font-family-heading;
    font-size: rem(12);
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  &__directory-title {
    margin: 0 0 rem(24);
    color: $text-primary;
    font-family: $font-family-heading;
    font-size: rem(26);
    font-weight: 800;
    line-height: 1.15;

    @include pc {
      font-size: rem(34);
    }
  }

  &__directory-grid {
    display: grid;
    gap: rem(16);

    @include pc {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  &__directory-card {
    padding: rem(20);
    border: 1px solid rgba($border-color, 1);
    border-radius: rem(20);
    background: rgba($bg-tertiary, 0.8);
    transition: transform $transition-speed, border-color $transition-speed, background $transition-speed;

    &:hover {
      transform: translateY(rem(-4));
      border-color: rgba($primary-color, 0.3);
      background: rgba($bg-tertiary, 0.95);
    }
  }

  &__directory-eyebrow {
    margin: 0 0 rem(10);
    color: $secondary-color;
    font-size: rem(11);
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__directory-card-title {
    margin: 0 0 rem(10);
    color: $text-primary;
    font-family: $font-family-heading;
    font-size: rem(19);
    font-weight: 800;
    line-height: 1.2;
  }

  &__directory-description {
    margin: 0;
    color: $text-secondary;
    font-size: rem(13);
    line-height: 1.65;
    @include text-ellipsis(3);
  }
}
</style>
